import { ReactNode, useMemo, useState } from "react";
import {
  useCreateMarketoIntegrationMutation,
  useLazyGetCRMListQuery,
} from "../../../../api";
import { ReuseModal } from "../../ReuseModal";
import { Stack, Typography } from "@mui/material";
import { ReuseInput } from "../../ReuseInput";
import { CRMListItem, MarketoLoginData } from "../../../../types/apiTypes";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { Integration } from "../../../../types/enums";

type Field = keyof MarketoLoginData;

const validation: Record<Field, (value: string) => boolean> = {
  munchkin_id: (v) => !v.trim(),
  client_id: (v) => !v.trim(),
  client_secret: (v) => !v.trim(),
};

const errorMessages: Record<Field, string> = {
  munchkin_id: "Munchkin ID is required",
  client_id: "Client ID is required",
  client_secret: "Client Secret is required",
};

const isKeyOfValidation = (field: string): field is Field => {
  return field in validation;
};

const requiredFields: Field[] = ["munchkin_id", "client_id", "client_secret"];

type Props = {
  onClose: () => void;
  syncModal: (crmList: CRMListItem[]) => ReactNode;
};

export const LoginMarketoModal = ({ onClose, syncModal }: Props) => {
  const [formData, setFormData] = useState<MarketoLoginData>({
    munchkin_id: "",
    client_id: "",
    client_secret: "",
  });

  const [errors, setErrors] = useState<Record<Field, boolean>>({
    munchkin_id: false,
    client_id: false,
    client_secret: false,
  });

  const [
    createMarketoIntegration,
    { isLoading: isCreatingMarketoIntegration },
  ] = useCreateMarketoIntegrationMutation();
  const [getCRMList, { isLoading: isListLoading, data }] =
    useLazyGetCRMListQuery();

  const isLoading = isCreatingMarketoIntegration || isListLoading;

  const inputChangeHandler = (field: string, value: string) => {
    if (isKeyOfValidation(field)) {
      setFormData((prev) => ({ ...prev, [field]: value }));

      setErrors((prev) => ({
        ...prev,
        [field]: validation[field](value),
      }));
    }
  };

  const submitHandler = () => {
    createMarketoIntegration(formData)
      .unwrap()
      .then(() => {
        getCRMList(Integration.marketo)
          .unwrap()
          .catch((err) => {
            toast.error(
              <CustomToast
                type={"error"}
                text={
                  getErrorMessage(err).globalError ||
                  "Something went wrong. Please try again"
                }
                title={"Failed Sync with Marketo"}
              />,
            );
          });
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={"Failed to Make Integration with Marketo"}
          />,
        );
      });
  };

  const isDisabled = useMemo(
    () =>
      Object.values(errors).some(Boolean) ||
      requiredFields.some((key) => !formData[key].trim()),
    [errors, formData, requiredFields],
  );

  return (
    <ReuseModal
      isOpen
      onClose={onClose}
      onSubmit={submitHandler}
      title="Sync with Marketo"
      maxWidth="540px"
      submitBtnText={"Next"}
      submitBtnColor="#1C3C6C"
      isDisabled={isDisabled || isLoading}
      isDisabledCancelBtn={isLoading}
      isLoading={isLoading}
      isHide={!!data}
    >
      <Stack gap="48px" pt="8px" pb="24px">
        <Typography
          variant="subtitle1"
          color="custom.textDarkBlue"
          fontWeight={500}
        >
          Connect your Marketo Engage account to sync campaign data. You'll need
          three credentials from your Marketo Admin panel.
        </Typography>
        <ReuseInput
          name="munchkin_id"
          handleInputChange={inputChangeHandler}
          errorMessage={errors.munchkin_id ? errorMessages.munchkin_id : ""}
          value={formData.munchkin_id}
          label="Munchkin ID"
          placeholder="Munchkin ID"
          isError={errors.munchkin_id}
          isDisabled={isLoading}
        />

        <ReuseInput
          name="client_id"
          handleInputChange={inputChangeHandler}
          errorMessage={errors.client_id ? errorMessages.client_id : ""}
          value={formData.client_id}
          label="Client ID"
          placeholder="Client ID"
          isError={errors.client_id}
          isDisabled={isLoading}
        />

        <ReuseInput
          name="client_secret"
          handleInputChange={inputChangeHandler}
          errorMessage={errors.client_secret ? errorMessages.client_secret : ""}
          value={formData.client_secret}
          label="Client Secret"
          placeholder="Client Secret"
          isError={errors.client_secret}
          isDisabled={isLoading}
        />
      </Stack>

      {data && syncModal(data)}
    </ReuseModal>
  );
};
